import { Button } from "@/components/ui/button.tsx"
import { FieldConfigCard } from "./field_config_card.tsx"
import { FieldConfigForm, FieldConfigFormData } from "./field_config_form.tsx"
import { OrgFieldConfig, CreateFieldConfigRequest, FieldType } from "@/api/org_field_config.tsx"
import { OrgOption } from "@/api/org_options.tsx"
import { api_fetch } from "@/api/client.tsx"
import { upsertOrgOptions } from "./org_options_save.ts"
import { useState, useEffect } from "preact/hooks"
import { Plus, Loader2 } from "lucide-react"
import { toast } from "@/components/ui/use-toast"

export function OrgFieldsSettings() {
  const [fieldConfigs, setFieldConfigs] = useState<OrgFieldConfig[]>([])
  const [orgOptions, setOrgOptions] = useState<Record<string, OrgOption[]>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showAddForm, setShowAddForm] = useState(false)
  const [newConfig, setNewConfig] = useState<FieldConfigFormData | null>(null)

  useEffect(() => {
    loadData()
  }, [])

  async function loadData() {
    setLoading(true)
    try {
      // Load field configs
      const configsResult = await api_fetch<OrgFieldConfig[]>("/org-field-configs")
      if (configsResult.ok) {
        setFieldConfigs(configsResult.value.data)
        
        // Load options for each field
        const optionsMap: Record<string, OrgOption[]> = {}
        for (const config of configsResult.value.data) {
          if (config.field_type === "dropdown") {
            const optionsResult = await api_fetch<OrgOption[]>("/org-options", {
              params: { field_name: config.field_name }
            })
            if (optionsResult.ok) {
              optionsMap[config.field_name] = optionsResult.value.data
            }
          }
        }
        setOrgOptions(optionsMap)
      }
    } catch (error) {
      console.error("Failed to load data:", error)
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load field configurations"
      })
    } finally {
      setLoading(false)
    }
  }

  function createNewFieldConfig(): FieldConfigFormData {
    return {
      field_name: "product_category",
      field_type: FieldType.Dropdown,
      display_name: "",
      display_order: fieldConfigs.length,
      is_active: true,
      table_contexts: ["deduction_table", "accounting_table", "split_form", "check_detail_table", "deduction_detail"],
      tempOptions: []
    }
  }

  function startAddField() {
    const newConfig = createNewFieldConfig()
    setNewConfig(newConfig)
    setShowAddForm(true)
  }

  async function saveNewFieldConfig() {
    if (!newConfig) return
    
    setSaving(true)
    try {
      const result = await api_fetch<OrgFieldConfig>("/org-field-configs", {
        method: "POST",
        body: newConfig
      })
      if (result.ok) {
        const createdConfig = result.value.data
        setFieldConfigs([...fieldConfigs, createdConfig])
        
        if (newConfig.field_type === "dropdown" && newConfig.tempOptions && newConfig.tempOptions.length > 0) {
          const newOptions: OrgOption[] = newConfig.tempOptions.map((opt) => ({
            id: opt.id,
            org_id: createdConfig.org_id,
            field_name: createdConfig.field_name,
            option_value: opt.value,
            display_order: opt.order,
            is_active: true,
            match_patterns: opt.matchPatterns || [],
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          }))
          const saved = await upsertOrgOptions(createdConfig.field_name, newOptions, [])
          setOrgOptions((prev) => ({ ...prev, [createdConfig.field_name]: saved }))
        }
        
        setShowAddForm(false)
        setNewConfig(null)
        toast({
          title: "Success",
          description: "Field configuration created"
        })
      } else {
        throw new Error("Failed to create field config")
      }
    } catch (error) {
      console.error("Failed to create field config:", error)
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create field configuration"
      })
    } finally {
      setSaving(false)
    }
  }

  function cancelAddField() {
    setShowAddForm(false)
    setNewConfig(null)
  }

  function syncFieldConfigInState(config: OrgFieldConfig) {
    setFieldConfigs((prev) => prev.map((fc) => (fc.id === config.id ? config : fc)))
  }

  async function deleteFieldConfig(configId: string): Promise<void> {
    setSaving(true)
    try {
      const result = await api_fetch(`/org-field-configs/${configId}`, {
        method: "DELETE"
      })
      if (result.ok) {
        setFieldConfigs(fieldConfigs.filter(fc => fc.id !== configId))
        toast({
          title: "Success",
          description: "Field configuration deleted"
        })
      } else {
        throw new Error("Failed to delete field config")
      }
    } catch (error) {
      console.error("Failed to delete field config:", error)
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete field configuration"
      })
      throw error // Re-throw so the calling component can handle it
    } finally {
      setSaving(false)
    }
  }

  async function updateOrgOptions(
    fieldName: string,
    options: OrgOption[],
    previousFieldName?: string
  ): Promise<void> {
    const lookupFieldName = previousFieldName ?? fieldName
    const existingOptions = orgOptions[lookupFieldName] || orgOptions[fieldName] || []

    setSaving(true)
    try {
      const saved = await upsertOrgOptions(fieldName, options, existingOptions)
      setOrgOptions((prev) => {
        const next = { ...prev, [fieldName]: saved }
        if (previousFieldName && previousFieldName !== fieldName) {
          delete next[previousFieldName]
        }
        return next
      })
    } catch (error) {
      console.error("Failed to update options:", error)
      throw error
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Loading field configurations...
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600">
            Configure custom fields that will appear in deduction tables, accounting tables, and split forms.
          </p>
        </div>
        <Button onClick={startAddField} disabled={saving}>
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Plus className="w-4 h-4 mr-2" />
          )}
          Add Field
        </Button>
      </div>

      {/* Add Field Form */}
      {showAddForm && newConfig && (
        <div className="border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium">Add New Field</h3>
            <div className="flex gap-2">
              <Button 
                // @ts-ignore
                size="sm" 
                onClick={saveNewFieldConfig}
                disabled={saving}>
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4 mr-1" />
                )}
                Save
              </Button>
              <Button 
                // @ts-ignore
                size="sm" 
                variant="outline" 
                onClick={cancelAddField}>
                Cancel
              </Button>
            </div>
          </div>
                  <FieldConfigForm
                    config={newConfig}
                    onUpdate={(updatedConfig) => {
                      setNewConfig(updatedConfig)
                    }}
                    showActiveField={false}
                  />
        </div>
      )}

      {/* Existing Field Configs */}
      {fieldConfigs.length === 0 && !showAddForm ? (
        <div className="text-center py-12">
          <div className="text-gray-500 mb-4">
            No field configurations found. Click "Add Field" to create your first custom field.
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {fieldConfigs
            .sort((a, b) => a.display_order - b.display_order)
            .map(config => (
              <FieldConfigCard
                key={config.id}
                config={config}
                options={orgOptions[config.field_name] || []}
                onUpdate={syncFieldConfigInState}
                onDelete={deleteFieldConfig}
                onUpdateOptions={updateOrgOptions}
              />
            ))}
        </div>
      )}
    </div>
  )
}



